import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import type { Pillar, PillarId } from '../staffPillars';

interface Props {
  pillars: Pillar[];
  activeId: PillarId | null;
  historyActive?: boolean;
}

/**
 * The pillars along the bottom of the screen, on phones, with History after them.
 *
 * Borrowed from the parent portal's `ModernMobileNav`, deliberately: a teacher who is also
 * a parent uses both apps on the same phone, and switching between them should not mean
 * switching how navigation works. The teacher portal keeps its hamburger for the things
 * that are not destinations — profile, sign out, the admin and parent portal links —
 * exactly as the parent portal keeps its own header.
 *
 * History sits last, after the rosters, because the order is the order of the day: what is
 * running now, then what already ran. It is the one entry that is always there — a teacher
 * between terms, or on a Saturday, has no roster to open and the archive is the only thing
 * left to ask, which is also why it alone can carry the bar to the two destinations below.
 *
 * Nothing renders below two destinations: a bar with one is a label, not navigation, and it
 * would cost a phone a permanent strip of screen for nothing.
 */
export function StaffBottomNav({ pillars, activeId, historyActive = false }: Props) {
  // History always counts as one destination, so a single pillar beside it is already a
  // choice. Only a teacher with no roster at all is left with a bar of one.
  if (pillars.length === 0) return null;

  const itemClass = (active: boolean) =>
    `flex flex-col items-center justify-center flex-1 py-1.5 px-1 rounded-lg transition-colors ${
      active ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <nav
      aria-label="Rosters"
      className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 pb-safe"
    >
      <div className="flex items-center justify-around px-2 py-1.5">
        {pillars.map(pillar => {
          const Icon = pillar.icon;
          const active = pillar.id === activeId;
          return (
            <Link
              key={pillar.id}
              to={pillar.path}
              aria-current={active ? 'page' : undefined}
              data-pillar={pillar.id}
              className={itemClass(active)}
            >
              <Icon className={`w-6 h-6 mb-0.5 ${active ? 'text-blue-600' : 'text-gray-500'}`} />
              {/* Up to five labels have to fit one phone width, so they wrap rather than
                  truncate — "After School" over two lines still reads. */}
              <span className="text-[11px] font-medium leading-tight text-center">
                {pillar.label}
              </span>
            </Link>
          );
        })}

        <Link
          to="/staff-app/history"
          aria-current={historyActive ? 'page' : undefined}
          data-nav="history"
          className={itemClass(historyActive)}
        >
          <History
            className={`w-6 h-6 mb-0.5 ${historyActive ? 'text-blue-600' : 'text-gray-500'}`}
          />
          <span className="text-[11px] font-medium leading-tight text-center">History</span>
        </Link>
      </div>
    </nav>
  );
}
